<template>
  <div class="mfd-wrapper">

    <!-- ── QUESTION PHASE ── -->
    <div v-if="phase === 'question'" class="mfd-question">
      <div class="mfd-question-icon">🗂️</div>
      <h2 class="mfd-question-title">Would you like to add your game's first data now?</h2>
      <p class="mfd-question-desc">
        A few quick steps to set up factions, subsections, and a first unit.<br>
      </p>
      <div class="mfd-question-actions">
        <button type="button" class="btn btn-secondary" style="background:#f85149;color:#fff" @click="$emit('answer-no')">No thanks</button>
        <button type="button" class="btn btn-primary" @click="$emit('wizard-start')">Create first data →</button>
      </div>
    </div>

    <!-- ── DONE PHASE ── -->
    <div v-else-if="phase === 'done'" class="mfd-done">
      <div class="mfd-done-icon">✅</div>
      <h2 class="mfd-done-title">First data saved!</h2>
      <p class="mfd-done-desc">
        Use the <strong>Done</strong> or <strong>Advanced config</strong> buttons below to proceed.
      </p>
      <div v-if="factions.length || firstUnitName" class="mfd-done-summary">
        <div v-if="factions.length" class="mfd-summary-row">
          <span class="mfd-summary-label">Factions</span>
          <span class="mfd-summary-value">{{ factions.join(', ') }}</span>
        </div>
        <div v-if="sections.length" class="mfd-summary-row">
          <span class="mfd-summary-label">Subsections</span>
          <span class="mfd-summary-value">{{ sections.join(', ') }}</span>
        </div>
        <div v-if="firstUnitName" class="mfd-summary-row">
          <span class="mfd-summary-label">First Unit</span>
          <span class="mfd-summary-value">{{ firstUnitName }}<template v-if="firstUnitFaction"> ({{ firstUnitFaction }}<template v-if="firstUnitSection"> › {{ firstUnitSection }}</template>)</template></span>
        </div>
        <div v-if="profileNames.length" class="mfd-summary-row">
          <span class="mfd-summary-label">Profiles</span>
          <span class="mfd-summary-value">{{ profileNames.join(', ') }}</span>
        </div>
        <div v-if="pickedSkin" class="mfd-summary-row mfd-summary-skin-row">
          <span class="mfd-summary-label">Model Skin</span>
          <span class="mfd-summary-value mfd-skin-value">
            <img :src="pickedSkin.thumbnailUrl" :alt="pickedSkin.name" class="mfd-skin-thumb" />
            {{ pickedSkin.name }}
          </span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'MyFirstDataEditor',

  props: {
    initialPhase:    { type: String, default: 'question' }, // 'question' | 'done'
    factions:        { type: Array,  default: () => [] },
    sections:        { type: Array,  default: () => [] },
    firstUnitName:   { type: String, default: '' },
    firstUnitFaction:{ type: String, default: '' },
    firstUnitSection:{ type: String, default: '' },
    profileNames:    { type: Array,  default: () => [] },
    pickedSkin:      { type: Object, default: null },
  },

  data() {
    return {
      phase: this.initialPhase,
    };
  },
};
</script>

<style scoped>
.mfd-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ── Progress dots ── */
.mfd-substep-dots {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.mfd-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: #30363d;
  border: 1px solid #484f58;
  transition: background 0.2s, border-color 0.2s;
}
.mfd-dot--active {
  background: #58a6ff;
  border-color: #58a6ff;
}
.mfd-dot--done {
  background: #3fb950;
  border-color: #3fb950;
}

/* ── Header ── */
.mfd-header {
  text-align: center;
}
.mfd-step-label {
  font-size: 0.72rem;
  color: #8b949e;
  margin: 0 0 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.mfd-prompt {
  font-size: 1.05rem;
  color: #e6edf3;
  margin: 0 0 0.25rem;
  font-weight: 600;
}
.mfd-hint {
  font-size: 0.82rem;
  color: #8b949e;
  margin: 0;
}

/* ── Content area ── */
.mfd-content {
  min-height: 130px;
}

.mfd-empty-hint {
  margin-bottom: 0.6rem;
}

/* string-list */
.mfd-list-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.mfd-list-row .field-input {
  flex: 1;
}
.mfd-remove-btn {
  background: none;
  border: none;
  color: #f85149;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 4px;
  flex-shrink: 0;
  line-height: 1;
}
.mfd-remove-btn:hover {
  background: rgba(248, 81, 73, 0.15);
}
.mfd-add-btn {
  margin-top: 0.25rem;
  font-size: 0.85rem;
}

/* unit-input */
.mfd-unit-row {
  max-width: 380px;
  margin-bottom: 0.75rem;
}

/* informational */
.mfd-info-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.mfd-info-card {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}
.mfd-info-icon {
  font-size: 1.5rem;
  line-height: 1;
  flex-shrink: 0;
}
.mfd-info-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.mfd-info-label {
  color: #e6edf3;
  font-size: 0.92rem;
}
.mfd-info-desc {
  color: #8b949e;
  font-size: 0.82rem;
  margin: 0;
}

/* ── Navigation bar ── */
.mfd-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #21262d;
}
.mfd-nav-right {
  display: flex;
  gap: 0.6rem;
  align-items: center;
}

/* ── Question phase ── */
.mfd-question {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 1rem 0;
}
.mfd-question-icon {
  font-size: 2.5rem;
  line-height: 1;
}
.mfd-question-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #e6edf3;
  margin: 0;
}
.mfd-question-desc {
  font-size: 0.85rem;
  color: #8b949e;
  margin: 0;
  line-height: 1.5;
}
.mfd-question-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

/* ── Done phase ── */
.mfd-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
  padding: 1rem 0;
}
.mfd-done-icon {
  font-size: 2rem;
  line-height: 1;
}
.mfd-done-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #3fb950;
  margin: 0;
}
.mfd-done-desc {
  font-size: 0.85rem;
  color: #8b949e;
  margin: 0;
}
.mfd-done-summary {
  width: 100%;
  max-width: 420px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  text-align: left;
}
.mfd-summary-row {
  display: flex;
  gap: 0.75rem;
  font-size: 0.83rem;
}
.mfd-summary-label {
  color: #8b949e;
  flex-shrink: 0;
  min-width: 80px;
}
.mfd-summary-value {
  color: #e6edf3;
}
.mfd-skin-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.mfd-skin-thumb {
  width: 48px;
  height: 48px;
  border-radius: 5px;
  object-fit: cover;
  border: 1px solid #30363d;
  flex-shrink: 0;
}
</style>
